import React, { useState } from "react";
import type { RcFile, UploadFile, UploadChangeParam } from "antd/es/upload/interface";
import {
  $Card,
  $Row,
  $Button,
  $Form,
  SplitDocuments,
  $Upload,
} from "us.common/components";
import { UploadOutlined } from "us.icons";
import { useTranslation } from "react-i18next";

type Props = {
  splitRef: React.RefObject<{ open: (file: File | { originFileObj?: File } | null | undefined) => void }>;
  onFilesSplit: (files: File[]) => void;
};

/**
@description
DocumentProcessingOptions Component
UI for document processing actions such as bulk import, upload merge PDFs and splitting.
Provides upload controls, file validation and invokes the split/import flows for processing.
Features:
- Bulk import handling with duplicate filtering
- Upload merge PDF flow and split helper
- File type and count constraints enforcement
@link https://unicorn-solutions.atlassian.net/browse/UPD-20
@author Keshan Nimnaka <keshana@unicorn-solutions.com>
@since 29/06/2026
*/
const DocumentProcessingOptions: React.FC<Props> = ({
  splitRef,
  onFilesSplit,
}) => {
  const [bulkFileList] = useState<UploadFile[]>([]);
  const { t } = useTranslation();

  const handleFiles = (files: RcFile[]) => {
    try {
      const pdfFiles = files.filter((f) => f && f.type === "application/pdf");
      const seenKeys = new Set<string>();
      const unique = pdfFiles.filter((f) => {
        // Include lastModified: filename + size alone can collide across
        // different source documents (e.g. multiple "document.pdf" uploads).
        const key = `${f.name}-${f.size}-${f.lastModified}`;
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });
      if (unique.length > 0) onFilesSplit(unique);
    } catch (err) {
      console.error("DocumentProcessingOptions: failed to process bulk files", err);
    }
  };

  return (
    <$Card bordered={true} className="processing-section-card">
      <$Row gutter={16}>
        <div className="processing-section-title">
          {t(
            "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.DOCUMENT_PROCESSING_OPTIONS",
          )}
        </div>
      </$Row>
      <$Row gutter={16}>
        <div className="home-actions-row ">
          <$Upload
            showUploadList={false}
            multiple
            accept="application/pdf"
            maxCount={10}
            beforeUpload={(file: RcFile, fileList: RcFile[]) => {
              try {
                // Ant Design calls beforeUpload once per file in a bulk drop.
                // Instead of a setTimeout debounce lock (race-prone and leaves
                // dangling timers if the component unmounts mid-flight), only
                // act on the first file in the batch snapshot, since fileList
                // is the same full array reference on every invocation.
                if (fileList[0] === file) {
                  handleFiles(fileList);
                }
              } catch (err) {
                console.error("DocumentProcessingOptions: beforeUpload failed", err);
              }
              return false;
            }}
            fileList={bulkFileList}
          >
            <$Button type="default" className="document-process-option-buttons">
              {t("US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.BULK_IMPORT")}
            </$Button>
          </$Upload>

          <$Upload
            showUploadList={false}
            onChange={(info: UploadChangeParam<UploadFile>) => {
              try {
                const file = info.file?.originFileObj ?? info.file;
                if (file) splitRef.current?.open(file as File);
              } catch (err) {
                console.error("DocumentProcessingOptions: split upload failed", err);
              }
            }}
            accept="application/pdf"
            maxCount={1}
          >
            <$Button
              icon={<UploadOutlined />}
              className="ml-3 document-process-option-buttons"
            >
              {t(
                "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.UPLOAD_MERGE_PDF",
              )}
            </$Button>
          </$Upload>

          <$Form component={false}>
            <SplitDocuments
              ref={splitRef}
              onFinish={(files: File[]) => onFilesSplit(files)}
            />
          </$Form>

          <$Button
            type="default"
            className="ml-3 document-process-option-buttons"
            disabled={true}
          >
            {t("US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.SCAN_DOCUMENTS")}
          </$Button>
        </div>
      </$Row>
      <$Row gutter={16}>
        <div className="home-subtext">
          {t("US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.ALLOWED_FILE_TYPES")}
        </div>
      </$Row>
    </$Card>
  );
};

export default DocumentProcessingOptions;